/**
 * The four non-feed tracking sheets: diaper, weight, pump, and sleep.
 * Each renders in the shared bottom Sheet and works in two modes:
 * quick logging (new event) and editing (prefilled, with Delete).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useT } from '../i18n/i18n';
import { newEventBase, useActions, useApp } from '../lib/store';
import { nowIso, toLocalIso } from '../lib/time';
import {
  displayToMl,
  lbOzToGrams,
  ML_PER_OZ,
  mlToDisplay,
  weightChangePct,
} from '../lib/units';
import { MAX_NORMAL_WEIGHT_LOSS_PCT } from '../lib/expectations';
import type {
  DiaperEvent,
  PumpEvent,
  SleepEvent,
  VolumeUnit,
  WeightContext,
  WeightEvent,
} from '../lib/types';
import { Icons, radioGroupNav, radioTabIndex, Sheet } from './ui';

// ── Small shared helpers ──────────────────────────────────────────────────

/** ISO (with offset) → value for <input type="datetime-local">. */
function isoToInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** datetime-local value → local-offset ISO. */
function inputToIso(value: string): string {
  return toLocalIso(new Date(value));
}

const G_PER_LB = lbOzToGrams(1, 0);
const G_PER_OZ_W = lbOzToGrams(0, 1);

/** ml → editable number string in the display unit. */
function mlToInputValue(ml: number | undefined, unit: VolumeUnit): string {
  if (ml === undefined) return '';
  if (unit === 'ml') return String(Math.round(ml));
  return String(Math.round((ml / ML_PER_OZ) * 100) / 100);
}

const inputCls =
  'w-full min-h-[44px] rounded-2xl border border-line bg-surface-2 px-4 py-3 text-ink outline-none focus:border-primary';

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-bold text-ink-soft">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-ink-faint">{hint}</span> : null}
    </label>
  );
}

function TimeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={label}>
      <input
        type="datetime-local"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputCls}
      />
    </Field>
  );
}

function NoteField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const t = useT();
  return (
    <Field label={t.forms.shared.note}>
      <input
        type="text"
        value={value}
        maxLength={500}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t.forms.shared.notePlaceholder}
        className={inputCls}
      />
    </Field>
  );
}

function SaveDeleteRow({
  onSave,
  onDelete,
  saveDisabled,
}: {
  onSave: () => void;
  onDelete?: () => void;
  saveDisabled?: boolean;
}) {
  const t = useT();
  return (
    <div className="mt-5 space-y-3">
      <button
        type="button"
        onClick={onSave}
        disabled={saveDisabled}
        className="btn-primary w-full min-h-[48px] px-6 py-3 text-base disabled:opacity-40"
      >
        {t.common.actions.save}
      </button>
      {onDelete ? (
        <button
          type="button"
          onClick={onDelete}
          className="w-full min-h-[44px] rounded-full bg-bad-soft px-6 py-2.5 font-bold text-bad"
        >
          {t.common.actions.delete}
        </button>
      ) : null}
    </div>
  );
}

/** Confirm-then-delete handler shared by all sheets. */
function useConfirmDelete(onClose: () => void) {
  const t = useT();
  const { deleteEvent } = useActions();
  return (id: string) => async () => {
    if (!window.confirm(t.common.actions.confirmDelete)) return;
    await deleteEvent(id);
    onClose();
  };
}

// ── Diaper ────────────────────────────────────────────────────────────────

type DiaperKind = 'wet' | 'dirty' | 'both';

function kindOf(e: DiaperEvent): DiaperKind {
  if (e.wet && e.dirty) return 'both';
  return e.dirty ? 'dirty' : 'wet';
}

function flagsOf(kind: DiaperKind): { wet: boolean; dirty: boolean } {
  return { wet: kind !== 'dirty', dirty: kind !== 'wet' };
}

const DIAPER_TONES: Record<DiaperKind, string> = {
  wet: 'bg-accent-soft text-accent',
  dirty: 'bg-warn-soft text-warn',
  both: 'bg-primary-soft text-primary',
};

export function DiaperSheet({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing?: DiaperEvent;
}) {
  const t = useT();
  const { device } = useApp();
  const { saveEvent } = useActions();
  const confirmDelete = useConfirmDelete(onClose);

  const [kind, setKind] = useState<DiaperKind | null>(null);
  const [adjusting, setAdjusting] = useState(false);
  const [time, setTime] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    setKind(editing ? kindOf(editing) : null);
    setAdjusting(false);
    setTime(isoToInput(editing ? editing.at : nowIso()));
    setNote(editing?.note ?? '');
  }, [open, editing]);

  // In quick mode a tap saves right away; otherwise it just selects.
  const explicit = !!editing || adjusting;

  const tap = async (k: DiaperKind) => {
    if (explicit) {
      setKind(k);
      return;
    }
    if (!device) return;
    await saveEvent({ ...newEventBase(device), type: 'diaper', ...flagsOf(k) });
    onClose();
  };

  const save = async () => {
    if (!kind || !time) return;
    const trimmed = note.trim();
    if (editing) {
      await saveEvent({
        ...editing,
        ...flagsOf(kind),
        at: inputToIso(time),
        note: trimmed || undefined,
        updatedAt: nowIso(),
      });
    } else {
      if (!device) return;
      await saveEvent({
        ...newEventBase(device),
        type: 'diaper',
        ...flagsOf(kind),
        at: inputToIso(time),
        note: trimmed || undefined,
      });
    }
    onClose();
  };

  const kinds: { key: DiaperKind; label: string }[] = [
    { key: 'wet', label: t.forms.diaper.wet },
    { key: 'dirty', label: t.forms.diaper.dirty },
    { key: 'both', label: t.forms.diaper.both },
  ];

  return (
    <Sheet open={open} onClose={onClose} title={editing ? t.forms.diaper.editTitle : t.forms.diaper.title}>
      <div className="space-y-4">
        <div>
          {explicit ? (
            <p className="mb-1.5 text-sm font-bold text-ink-soft">{t.forms.diaper.kindLabel}</p>
          ) : null}
          <div
            role={explicit ? 'radiogroup' : undefined}
            aria-label={t.forms.diaper.kindLabel}
            onKeyDown={explicit ? (e) => radioGroupNav(e, (i) => setKind(kinds[i].key)) : undefined}
            className="grid grid-cols-3 gap-3"
          >
            {kinds.map((k, i) => {
              const selected = explicit && kind === k.key;
              return (
                <button
                  key={k.key}
                  type="button"
                  role={explicit ? 'radio' : undefined}
                  aria-checked={explicit ? selected : undefined}
                  tabIndex={explicit ? radioTabIndex(selected, i, kind !== null) : undefined}
                  onClick={() => void tap(k.key)}
                  className={`min-h-[88px] rounded-3xl border-2 py-6 text-lg font-bold transition-transform active:scale-95 ${
                    DIAPER_TONES[k.key]
                  } ${selected ? 'border-primary' : 'border-transparent'}`}
                >
                  {k.label}
                </button>
              );
            })}
          </div>
          {!explicit ? (
            <p className="mt-2 text-center text-sm text-ink-faint">{t.forms.diaper.quickHint}</p>
          ) : null}
        </div>

        {!editing && !adjusting ? (
          <button
            type="button"
            onClick={() => setAdjusting(true)}
            className="flex w-full min-h-[44px] items-center justify-between rounded-2xl bg-surface-2 px-4 py-3 text-sm font-bold text-ink-soft"
          >
            {t.forms.shared.adjust}
            {Icons.chevronRight('h-5 w-5')}
          </button>
        ) : null}

        {explicit ? (
          <>
            <TimeField label={t.forms.shared.time} value={time} onChange={setTime} />
            <NoteField value={note} onChange={setNote} />
            <SaveDeleteRow
              onSave={() => void save()}
              onDelete={editing ? () => void confirmDelete(editing.id)() : undefined}
              saveDisabled={!kind || !time}
            />
          </>
        ) : null}
      </div>
    </Sheet>
  );
}

// ── Weight ────────────────────────────────────────────────────────────────

const WEIGHT_CONTEXTS: WeightContext[] = ['birth', 'hospital', 'office', 'home'];

export function WeightSheet({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing?: WeightEvent;
}) {
  const t = useT();
  const { device, settings, profile } = useApp();
  const { saveEvent } = useActions();
  const confirmDelete = useConfirmDelete(onClose);

  const [lb, setLb] = useState('');
  const [oz, setOz] = useState('');
  const [g, setG] = useState('');
  const [context, setContext] = useState<WeightContext>('home');
  const [time, setTime] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    if (editing) {
      if (settings.weightUnit === 'lboz') {
        let wholeLb = Math.floor(editing.grams / G_PER_LB);
        let remOz = Math.round(((editing.grams - wholeLb * G_PER_LB) / G_PER_OZ_W) * 10) / 10;
        // Rounding can land on exactly 16 oz; roll it into the next pound
        // (matching gramsToLbOzParts in SettingsScreen).
        if (remOz >= 16) {
          wholeLb += 1;
          remOz = 0;
        }
        setLb(String(wholeLb));
        setOz(String(remOz));
        setG('');
      } else {
        setG(String(Math.round(editing.grams)));
        setLb('');
        setOz('');
      }
      setContext(editing.context ?? 'home');
      setTime(isoToInput(editing.at));
      setNote(editing.note ?? '');
    } else {
      setLb('');
      setOz('');
      setG('');
      setContext('home');
      setTime(isoToInput(nowIso()));
      setNote('');
    }
  }, [open, editing, settings.weightUnit]);

  const grams =
    settings.weightUnit === 'lboz'
      ? lbOzToGrams(parseFloat(lb) || 0, parseFloat(oz) || 0)
      : parseFloat(g) || 0;

  const birthG = profile?.birthWeightG;
  const pct = birthG && grams > 0 ? weightChangePct(grams, birthG) : null;

  const save = async () => {
    if (grams <= 0 || !time) return;
    const trimmed = note.trim();
    const fields = {
      grams: Math.round(grams),
      context,
      at: inputToIso(time),
      note: trimmed || undefined,
    };
    if (editing) {
      await saveEvent({ ...editing, ...fields, updatedAt: nowIso() });
    } else {
      if (!device) return;
      await saveEvent({ ...newEventBase(device), type: 'weight', ...fields });
    }
    onClose();
  };

  const contextLabels: Record<WeightContext, string> = t.forms.weight.contexts;

  return (
    <Sheet open={open} onClose={onClose} title={editing ? t.forms.weight.editTitle : t.forms.weight.title}>
      <div className="space-y-4">
        <Field label={t.forms.weight.weightLabel}>
          {settings.weightUnit === 'lboz' ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                min="0"
                step="any"
                value={lb}
                onChange={(e) => setLb(e.target.value)}
                className={inputCls}
                aria-label={`${t.forms.weight.weightLabel} (${t.forms.units.lb})`}
              />
              <span className="shrink-0 text-sm font-bold text-ink-soft">{t.forms.units.lb}</span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                value={oz}
                onChange={(e) => setOz(e.target.value)}
                className={inputCls}
                aria-label={`${t.forms.weight.weightLabel} (${t.forms.units.oz})`}
              />
              <span className="shrink-0 text-sm font-bold text-ink-soft">{t.forms.units.oz}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                min="0"
                step="any"
                value={g}
                onChange={(e) => setG(e.target.value)}
                className={inputCls}
                aria-label={`${t.forms.weight.weightLabel} (${t.forms.units.g})`}
              />
              <span className="shrink-0 text-sm font-bold text-ink-soft">{t.forms.units.g}</span>
            </div>
          )}
        </Field>

        {pct !== null && Math.abs(pct) >= 0.05 ? (
          <div
            className={`rounded-2xl px-4 py-3 ${
              pct >= -MAX_NORMAL_WEIGHT_LOSS_PCT ? 'bg-good-soft' : 'bg-warn-soft'
            }`}
          >
            <p
              className={`font-bold ${
                pct >= -MAX_NORMAL_WEIGHT_LOSS_PCT ? 'text-good' : 'text-warn'
              }`}
            >
              {pct < 0
                ? t.forms.weight.belowBirth(Math.abs(pct).toFixed(1))
                : t.forms.weight.aboveBirth(pct.toFixed(1))}
            </p>
            <p className="mt-1 text-sm text-ink-soft">{t.content.expectations.weightNote}</p>
            {pct < -MAX_NORMAL_WEIGHT_LOSS_PCT ? (
              <p className="mt-1 text-sm font-bold text-warn">{t.forms.weight.checkInNote}</p>
            ) : null}
          </div>
        ) : null}

        <div>
          <p className="mb-1.5 text-sm font-bold text-ink-soft">{t.forms.weight.contextLabel}</p>
          <div
            role="radiogroup"
            aria-label={t.forms.weight.contextLabel}
            onKeyDown={(e) => radioGroupNav(e, (i) => setContext(WEIGHT_CONTEXTS[i]))}
            className="grid grid-cols-2 gap-2"
          >
            {WEIGHT_CONTEXTS.map((c, i) => {
              const selected = context === c;
              return (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  tabIndex={radioTabIndex(selected, i, true)}
                  onClick={() => setContext(c)}
                  className={`min-h-[44px] rounded-2xl border px-3 py-2.5 text-sm font-bold ${
                    selected
                      ? 'border-primary bg-primary-soft text-primary'
                      : 'border-line bg-surface-2 text-ink-soft'
                  }`}
                >
                  {contextLabels[c]}
                </button>
              );
            })}
          </div>
        </div>

        <TimeField label={t.forms.shared.time} value={time} onChange={setTime} />
        <NoteField value={note} onChange={setNote} />
        <SaveDeleteRow
          onSave={() => void save()}
          onDelete={editing ? () => void confirmDelete(editing.id)() : undefined}
          saveDisabled={grams <= 0 || !time}
        />
      </div>
    </Sheet>
  );
}

// ── Pump ──────────────────────────────────────────────────────────────────

export function PumpSheet({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing?: PumpEvent;
}) {
  const t = useT();
  const { device, settings } = useApp();
  const { saveEvent } = useActions();
  const confirmDelete = useConfirmDelete(onClose);

  const [left, setLeft] = useState('');
  const [right, setRight] = useState('');
  const [minutes, setMinutes] = useState('');
  const [time, setTime] = useState('');
  const [note, setNote] = useState('');

  const unit = settings.volumeUnit;
  const unitLabel = unit === 'ml' ? t.forms.units.ml : t.forms.units.oz;

  useEffect(() => {
    if (!open) return;
    setLeft(mlToInputValue(editing?.leftMl, unit));
    setRight(mlToInputValue(editing?.rightMl, unit));
    setMinutes(
      editing?.durationSeconds !== undefined
        ? String(Math.round(editing.durationSeconds / 60))
        : '',
    );
    setTime(isoToInput(editing ? editing.at : nowIso()));
    setNote(editing?.note ?? '');
  }, [open, editing, unit]);

  const parseAmount = (raw: string): number | undefined => {
    if (raw.trim() === '') return undefined;
    const n = Math.max(0, parseFloat(raw) || 0);
    return displayToMl(n, unit);
  };

  const leftMl = parseAmount(left);
  const rightMl = parseAmount(right);
  const totalMl = (leftMl ?? 0) + (rightMl ?? 0);
  const mins = minutes.trim() === '' ? 0 : Math.max(0, Math.round(parseFloat(minutes) || 0));
  const durationSeconds = mins > 0 ? mins * 60 : undefined;

  const canSave = !!time && (leftMl !== undefined || rightMl !== undefined || durationSeconds !== undefined);

  const save = async () => {
    if (!canSave) return;
    const trimmed = note.trim();
    const fields = {
      leftMl,
      rightMl,
      durationSeconds,
      at: inputToIso(time),
      note: trimmed || undefined,
    };
    if (editing) {
      await saveEvent({ ...editing, ...fields, updatedAt: nowIso() });
    } else {
      if (!device) return;
      await saveEvent({ ...newEventBase(device), type: 'pump', ...fields });
    }
    onClose();
  };

  return (
    <Sheet open={open} onClose={onClose} title={editing ? t.forms.pump.editTitle : t.forms.pump.title}>
      <div className="space-y-4">
        <div>
          <p className="mb-1.5 text-sm font-bold text-ink-soft">{t.forms.pump.amountsLabel}</p>
          <div className="flex items-start gap-3">
            <label className="flex-1">
              <span className="mb-1 block text-xs font-bold text-ink-faint">
                {t.common.sides.left}
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  value={left}
                  onChange={(e) => setLeft(e.target.value)}
                  className={inputCls}
                />
                <span className="shrink-0 text-sm font-bold text-ink-soft">{unitLabel}</span>
              </div>
            </label>
            <label className="flex-1">
              <span className="mb-1 block text-xs font-bold text-ink-faint">
                {t.common.sides.right}
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  value={right}
                  onChange={(e) => setRight(e.target.value)}
                  className={inputCls}
                />
                <span className="shrink-0 text-sm font-bold text-ink-soft">{unitLabel}</span>
              </div>
            </label>
          </div>
          {totalMl > 0 ? (
            <p className="mt-2 rounded-2xl bg-primary-soft px-4 py-2.5 text-sm font-bold text-primary">
              {t.forms.pump.total(mlToDisplay(totalMl, unit))}
            </p>
          ) : null}
        </div>

        <Field label={t.forms.pump.durationLabel}>
          <input
            type="number"
            inputMode="numeric"
            min="0"
            step="1"
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            className={inputCls}
          />
        </Field>

        <TimeField label={t.forms.shared.time} value={time} onChange={setTime} />
        <NoteField value={note} onChange={setNote} />
        <SaveDeleteRow
          onSave={() => void save()}
          onDelete={editing ? () => void confirmDelete(editing.id)() : undefined}
          saveDisabled={!canSave}
        />
      </div>
    </Sheet>
  );
}

// ── Sleep ─────────────────────────────────────────────────────────────────

export function SleepSheet({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing?: SleepEvent;
}) {
  const t = useT();
  const { device } = useApp();
  const { saveEvent } = useActions();
  const confirmDelete = useConfirmDelete(onClose);

  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    setStart(isoToInput(editing ? editing.at : nowIso()));
    setEnd(editing?.endAt ? isoToInput(editing.endAt) : '');
    setNote(editing?.note ?? '');
  }, [open, editing]);

  const startMs = start ? new Date(start).getTime() : NaN;
  const endMs = end ? new Date(end).getTime() : NaN;
  const rangeInvalid = !!start && !!end && endMs <= startMs;
  const durationMins =
    !!start && !!end && !rangeInvalid ? Math.round((endMs - startMs) / 60_000) : null;

  const ongoingEdit = !!editing && !editing.endAt;

  const buildFields = (endIso?: string) => ({
    at: inputToIso(start),
    endAt: endIso,
    note: note.trim() || undefined,
  });

  /** Quick mode: log that baby just fell asleep, no end time yet. */
  const fellAsleep = async () => {
    if (!device || !start) return;
    await saveEvent({ ...newEventBase(device), type: 'sleep', ...buildFields(undefined) });
    onClose();
  };

  /** Prominent action while editing an ongoing sleep. */
  const wokeUp = async () => {
    if (!editing || !start) return;
    // Carry the on-screen start/note edits along with the wake-up instead
    // of silently discarding them.
    await saveEvent({ ...editing, ...buildFields(nowIso()), updatedAt: nowIso() });
    onClose();
  };

  const save = async () => {
    if (!start || rangeInvalid) return;
    const fields = buildFields(end ? inputToIso(end) : undefined);
    if (editing) {
      await saveEvent({ ...editing, ...fields, updatedAt: nowIso() });
    } else {
      if (!device) return;
      await saveEvent({ ...newEventBase(device), type: 'sleep', ...fields });
    }
    onClose();
  };

  return (
    <Sheet open={open} onClose={onClose} title={editing ? t.forms.sleep.editTitle : t.forms.sleep.title}>
      <div className="space-y-4">
        {!editing ? (
          <button
            type="button"
            onClick={() => void fellAsleep()}
            disabled={!start}
            className="btn-primary w-full min-h-[56px] px-6 py-4 text-lg disabled:opacity-40"
          >
            {t.forms.sleep.fellAsleepNow}
          </button>
        ) : null}

        {ongoingEdit ? (
          <>
            <div className="flex items-center gap-2.5 rounded-2xl bg-primary-soft px-4 py-3">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-primary animate-pulse-soft" aria-hidden="true" />
              <p className="text-sm font-bold text-primary">{t.forms.sleep.sleepingNow}</p>
            </div>
            <button
              type="button"
              onClick={() => void wokeUp()}
              disabled={!start}
              className="btn-primary w-full min-h-[56px] px-6 py-4 text-lg disabled:opacity-40"
            >
              {t.forms.sleep.wokeUpNow}
            </button>
          </>
        ) : null}

        <TimeField label={t.forms.sleep.startLabel} value={start} onChange={setStart} />

        <div>
          <Field label={t.forms.sleep.endLabel} hint={!end ? t.forms.sleep.endOptionalHint : undefined}>
            <input
              type="datetime-local"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className={inputCls}
            />
          </Field>
          {rangeInvalid ? (
            <p className="mt-1.5 text-sm font-bold text-bad">{t.forms.sleep.endBeforeStart}</p>
          ) : null}
          {durationMins !== null ? (
            <p className="mt-2 rounded-2xl bg-accent-soft px-4 py-2.5 text-sm font-bold text-accent">
              {t.forms.sleep.duration(
                t.common.time.durationHM(Math.floor(durationMins / 60), durationMins % 60),
              )}
            </p>
          ) : null}
        </div>

        <NoteField value={note} onChange={setNote} />
        <SaveDeleteRow
          onSave={() => void save()}
          onDelete={editing ? () => void confirmDelete(editing.id)() : undefined}
          saveDisabled={!start || rangeInvalid}
        />
      </div>
    </Sheet>
  );
}
